import { useEffect, useState } from 'react';
import api from '../../services/api.js';

/**
 * Always-visible remaining-budget indicator (Higgsfield "credit meter always
 * visible" — an ADAPT pattern: seeing the number is what lets a creative
 * self-regulate, so a quota never arrives as a surprise 429 mid-task).
 *
 * Shows REMAINING, never a purchase prompt. Renders nothing at all when no
 * quota applies — an invented denominator would be a fabrication (W1).
 */
const LABELS = { generations: 'generations', costCents: 'budget', gpuSeconds: 'GPU-seconds' };

const REFRESH_MS = 60_000;

export function formatRemaining(meter) {
  if (meter.metric === 'costCents') return `$${(meter.remaining / 100).toFixed(2)}`;
  if (meter.metric === 'gpuSeconds') return `${Math.round(meter.remaining)}s`;
  return String(meter.remaining);
}

export default function BudgetMeter({ className = '' }) {
  const [budget, setBudget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const body = await api.request('/usage/budget');
        if (!cancelled) setBudget(body?.data ?? body ?? null);
      } catch {
        // A budget we cannot read is not a budget of zero — render nothing.
        if (!cancelled) setBudget(null);
      }
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const meter = budget?.headlineMeter;
  if (!budget?.measured || !meter) return null;

  return (
    <div
      className={`budget-meter budget-meter--${meter.tone} ${className}`.trim()}
      title={`${meter.scopeLabel} — ${meter.consumed} of ${meter.limit} used this month`}
    >
      <span className="budget-meter__label">
        {formatRemaining(meter)} {LABELS[meter.metric] ?? meter.metric} left
      </span>
      <progress className="budget-meter__bar" max={100} value={Math.min(100, meter.percentUsed)} />
    </div>
  );
}
